import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useApiQuery } from "@/lib/useApiQuery";
import { Building2, Gauge, Zap, ArrowUpRight, Layers } from "lucide-react";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { NoProjectAccess } from "@/components/AppShell";
import { FaultsSummary } from "@/components/FaultsSummary";

function formatArea(area) {
  if (!area) return "—";
  if (typeof area === "object" && area.val != null) {
    return `${Number(area.val).toLocaleString()} ${area.unit || ""}`.trim();
  }
  return String(area);
}

function KpiTile({ label, value, unit, testId }) {
  return (
    <div className="k-card p-6" data-testid={testId}>
      <div className="k-sublabel">{label}</div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-4xl font-semibold font-heading text-[#156F5B]">{value}</span>
        {unit && <span className="text-xs text-[#6B7280] font-heading">{unit}</span>}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { project } = useProject();
  const params = useMemo(() => (project ? { project } : {}), [project]);
  const { data: siteData, loading } = useApiQuery(["sites", params], "/sites", { params });
  const { data: health } = useApiQuery(["datasourceHealth"], "/datasource/health");
  const { data: sync } = useApiQuery(["syncStatus"], "/sync/status");
  const sites = useMemo(() => siteData || [], [siteData]);

  const totalArea = useMemo(() => {
    return sites.reduce((acc, s) => {
      const a = s.area;
      if (a && typeof a === "object" && a.val != null) return acc + Number(a.val);
      return acc;
    }, 0);
  }, [sites]);

  let cardIndex = -1;

  const projectGroups = useMemo(() => {
    const map = new Map();
    for (const s of sites) {
      const key = s.project || "Unassigned";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [sites]);

  const { user } = useAuth();
  if (user?.role !== "admin" && (user?.projects || []).length === 0) return <NoProjectAccess />;
  return (
    <div className="max-w-7xl fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="k-label">Portfolio</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-bold font-display text-[#212121]">Overview</h1>
        </div>
        <div className="text-right">
          <div className="k-sublabel">Data Feed</div>
          <div className="mt-1 text-sm font-heading font-semibold">
            {health?.ok ? (
              <span className="text-[#156F5B]">● Live</span>
            ) : health ? (
              <span className="text-red-600">● offline</span>
            ) : (
              <span className="text-[#9CA3AF]">…</span>
            )}
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiTile label="Sites" value={loading ? "…" : sites.length} testId="kpi-sites" />
        <KpiTile label="Projects" value={loading ? "…" : projectGroups.length} testId="kpi-projects" />
        <KpiTile
          label="Total Area"
          value={loading ? "…" : Math.round(totalArea).toLocaleString()}
          unit="ft²"
          testId="kpi-area"
        />
        <KpiTile
          label="Data Points"
          value={sync?.points_synced != null ? sync.points_synced.toLocaleString() : "—"}
          testId="kpi-points"
        />
        <KpiTile
          label="Last Sync"
          value={sync?.finished_at ? new Date(sync.finished_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
          unit="hourly"
          testId="kpi-last-sync"
        />
      </div>

      {/* Sites grid */}
      <div className="mt-12">
        <div className="flex items-baseline justify-between">
          <h2 className="text-2xl font-bold font-heading text-[#212121]">Sites</h2>
          <Link to="/sites" className="text-xs font-heading font-semibold text-[#156F5B] hover:underline" data-testid="see-all-sites-link">
            See all →
          </Link>
        </div>
        {loading ? (
          <div className="mt-6 k-sublabel">Loading…</div>
        ) : sites.length === 0 ? (
          <div className="mt-6 k-card p-8 text-[#6B7280] text-sm" data-testid="no-sites">
            No sites synced yet. Connect a project from Data Sources, or wait for the hourly sync.
          </div>
        ) : (
          <div className="mt-6 space-y-8">
            {projectGroups.map(([project, list]) => (
              <div key={project} data-testid={`overview-project-group-${project}`}>
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-[#156F5B]" />
                  <h3 className="text-base font-heading font-bold text-[#212121]">{project}</h3>
                  <span className="text-[11px] font-heading font-semibold text-[#156F5B] bg-[#156F5B]/10 border border-[#156F5B]/20 rounded-full px-2 py-0.5">
                    {list.length} site{list.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {list.map((s) => {
                    cardIndex += 1;
                    const i = cardIndex;
                    return (
                      <Link
                        key={s.site_id}
                        to={`/sites/${encodeURIComponent(s.site_id)}`}
                        className="group k-card block overflow-hidden hover:-translate-y-1 hover:shadow-md"
                        style={{ transition: "transform .2s ease, box-shadow .2s ease" }}
                        data-testid={`site-card-${i}`}
                      >
                        <div className="h-1.5 bg-gradient-to-r from-[#156F5B] to-[#4C9F87]" />
                        <div className="p-6">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="flex items-center gap-2 k-sublabel mb-2">
                                <Building2 className="h-3 w-3" /> Site {String(i + 1).padStart(2, "0")}
                              </div>
                              <div className="text-xl font-heading font-semibold text-[#212121] group-hover:text-[#156F5B]" style={{ transition: "color .2s ease" }}>
                                {s.dis}
                              </div>
                              <div className="mt-1 text-xs text-[#6B7280]">
                                {[s.geo_city, s.geo_state].filter(Boolean).join(", ") || "—"}
                              </div>
                            </div>
                            <ArrowUpRight className="h-4 w-4 text-[#9CA3AF] group-hover:text-[#156F5B]" style={{ transition: "color .2s ease" }} />
                          </div>
                          <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
                            <div className="rounded-md bg-[#F3F8F7] p-3">
                              <div className="k-sublabel flex items-center gap-1"><Gauge className="h-3 w-3" /> Area</div>
                              <div className="mt-1 font-heading font-semibold text-[#212121]">{formatArea(s.area)}</div>
                            </div>
                            <div className="rounded-md bg-[#F3F8F7] p-3">
                              <div className="k-sublabel flex items-center gap-1"><Zap className="h-3 w-3" /> TZ</div>
                              <div className="mt-1 font-heading font-semibold text-[#212121]">{s.tz || "—"}</div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <FaultsSummary />
    </div>
  );
}
